import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {reviewButtonStyle, REVIEW_BUTTON_STYLE_FOR_STATUS} from 'notebook/reviews/ReviewActionButton';
import {ReviewBadgeStatus} from 'notebook/reviews/ReviewConstants';
import {isWorksheetStatusActionable} from 'notebook/worksheets/util/worksheetControl';
import {WorksheetTaskStatusType} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {usePMEIsAndMethodTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import TransitionRunModal, {TransitionStatusType} from 'procedures/worksheets/TransitionRunModal';
import EntityStore from 'stores/EntityStore';
import {FONT_WEIGHT_PROXIMA_REGULAR, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {AsyncButtonLoadingBehavior, ControlledAsyncButton} from 'ui-library/buttons/AsyncButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const START_RUN_TEXT = 'Start run';
const COMPLETE_RUN_TEXT = 'Complete run';
const FAIL_RUN_TEXT = 'Fail run';
const SKIP_RUN_TEXT = 'Skip run';

// worksheet-supported statuses to override the ReviewBadge
export const worksheetStatusStyles = StyleSheet.create({
  skipped: {
    backgroundColor: Color.GRAPE_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.GRAPE_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.GRAPE_400},
  },
  pending: {
    backgroundColor: Color.GRAY_600,
    ':hover:not(.is-disabled)': {backgroundColor: Color.GRAY_550},
    ':active:not(.is-disabled)': {backgroundColor: Color.GRAY_500},
  },
  gray: {backgroundColor: Color.GRAY_400},
});

const TASK_STATUS_TO_BUTTON_PROPS: Record<
  WorksheetTaskStatusType,
  {
    text: string;
    className: string;
    icon?: BenchlingIconName;
    useLoadingSpinner?: boolean;
  }
> = {
  [WorksheetTaskStatusType.PENDING]: {
    text: 'Pending',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.pending),
    icon: 'request-pending',
  },
  [WorksheetTaskStatusType.STARTING]: {
    text: 'Starting...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.IN_PROGRESS],
      reviewButtonStyle.loadingButton
    ),
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.IN_PROGRESS]: {
    text: 'In Progress',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.IN_PROGRESS]),
    icon: 'timer',
  },
  [WorksheetTaskStatusType.COMPLETING]: {
    text: 'Completing...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.ACCEPTED],
      reviewButtonStyle.loadingButton
    ),
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.COMPLETED]: {
    text: 'Completed',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.ACCEPTED]),
    icon: 'done',
  },
  [WorksheetTaskStatusType.FAILING]: {
    text: 'Failing...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.REJECTED],
      reviewButtonStyle.loadingButton
    ),
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.FAILED]: {
    text: 'Failed',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.REJECTED]),
    icon: 'status-failed',
  },
  [WorksheetTaskStatusType.SKIPPING]: {
    text: 'Skipping...',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.skipped, reviewButtonStyle.loadingButton),
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    text: 'Skipped',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.skipped),
    icon: 'forward-step',
  },
  // this state is used for Worksheets whose previous unit operation have not been completed
  [WorksheetTaskStatusType.NOT_READY]: {
    text: 'Pending',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.gray),
    icon: 'request-pending',
  },
  // A planned run is waiting on its previous unit operation
  [WorksheetTaskStatusType.PLANNED]: {
    text: 'Planned',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.gray),
    icon: 'calendar',
  },
  // Note: We will not have the following statuses for Method tasks, but this keeps TypeScript happy since all
  // possible keys for WorksheetTaskStatusType are fulfilled
  [WorksheetTaskStatusType.READY]: {text: 'Pending', className: css(reviewButtonStyle.button)},
  [WorksheetTaskStatusType.AMENDING]: {
    text: 'Amending...',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.gray),
    useLoadingSpinner: true,
  },
};

function getStatusSummaryDropdown(status: WorksheetTaskStatusType, timestamp?: string): Array<JSX.Element> {
  return [
    <Option contentClassName={css(styles.dropdownOptionContent)} isHovered={false} key="status-info">
      <div className={css(styles.dropdownDetails)}>
        <div>
          <span className={css(styles.statusText)}>{TASK_STATUS_TO_BUTTON_PROPS[status].text}</span>
          <br />
          <span className={css(styles.timestampText)}>{timestamp}</span>
        </div>
      </div>
    </Option>,
  ];
}

const statusMappings: Record<string, {text: string; status: TransitionStatusType}> = {
  start: {text: START_RUN_TEXT, status: WorksheetTaskStatusType.IN_PROGRESS},
  complete: {text: COMPLETE_RUN_TEXT, status: WorksheetTaskStatusType.COMPLETED},
  fail: {text: FAIL_RUN_TEXT, status: WorksheetTaskStatusType.FAILED},
  skip: {text: SKIP_RUN_TEXT, status: WorksheetTaskStatusType.SKIPPED},
};

function makeRunOption(key: string, openModal: (status: TransitionStatusType) => void): JSX.Element {
  const {text, status} = statusMappings[key];
  return (
    <Option
      key={`${key}-run`}
      dataTestAttrs={{component: 'WorksheetStatusDropdown', element: `${key}RunOption`}}
      onSelect={() => {
        openModal(status);
      }}
    >
      <span className={css(styles.optionText)}>{text}</span>
    </Option>
  );
}

function getDropdownOptions(
  status: WorksheetTaskStatusType | undefined,
  openModal: (status: TransitionStatusType) => void,
  timestamp?: string
): Array<JSX.Element> | undefined {
  if (!status || !isWorksheetStatusActionable(status)) {
    return undefined;
  }

  switch (status) {
    case WorksheetTaskStatusType.PENDING:
      return ['start', 'fail', 'skip'].map((key) => makeRunOption(key, openModal));
    case WorksheetTaskStatusType.IN_PROGRESS:
      return ['complete', 'fail', 'skip'].map((key) => makeRunOption(key, openModal));
    default:
      return getStatusSummaryDropdown(status, timestamp);
  }
}

interface WorksheetStatusButtonProps {
  disabled?: boolean;
  name: string;
  isDropdown?: boolean;
}

export default function WorksheetStatusButton({
  disabled,
  name,
  isDropdown,
}: WorksheetStatusButtonProps): JSX.Element {
  const {worksheetTaskData, worksheet, refetch, disableTransitionRunModalCompleteRunButton} =
    WorksheetContext.useContext();
  const {status, timestamp} = worksheetTaskData;
  const isActionable = status != null && isWorksheetStatusActionable(status);
  const displayStatus =
    status ?? (disabled ? WorksheetTaskStatusType.NOT_READY : WorksheetTaskStatusType.PENDING);
  const buttonProps = TASK_STATUS_TO_BUTTON_PROPS[displayStatus];
  // Read PMEIs (and their method tasks) here so the in-tree modal always receives the latest data. When a run
  // is started under delayed mapping, the worksheet's realtime subscription refetches PMEIs as tasks are
  // created, re-rendering this component and letting the modal detect when it can stop showing "Starting".
  const pmeisAndTasks = usePMEIsAndMethodTasks();

  const [transitionModalStatus, setTransitionModalStatus] = useState<TransitionStatusType | null>(null);
  const closeTransitionModal = useCallback(() => {
    setTransitionModalStatus(null);
  }, []);

  const dropdownOptions = getDropdownOptions(status, setTransitionModalStatus, timestamp);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const onToggleDropdown = (newDropdownState: boolean): void => {
    if (newDropdownState !== isDropdownOpen) {
      setIsDropdownOpen(newDropdownState);
    }
  };

  return (
    <>
      {transitionModalStatus != null && (
        <TransitionRunModal
          name={name}
          currentUser={EntityStore.currentUser()}
          transitionToStatus={transitionModalStatus}
          worksheetTaskStatus={status}
          worksheet={worksheet}
          onTaskUpdate={refetch}
          disableCompleteRunButton={disableTransitionRunModalCompleteRunButton}
          pmeisAndTasks={pmeisAndTasks}
          onRequestClose={closeTransitionModal}
        />
      )}
      <Dropdown
        isOpen={isDropdownOpen}
        onToggle={onToggleDropdown}
        menu={
          <Menu width={MenuWidth.FIT_CONTENT} className={css(styles.dropdownRightAligned)}>
            {dropdownOptions}
          </Menu>
        }
      >
        {buttonProps.useLoadingSpinner ? (
          // A controlled async button is used here since the onClick function of the Button below is not only
          // async but it is also not limited to the duration of a singular request or task. Instead, we rely
          // on realtime messages to know when each of the individual tasks have transitioned. As such, we use
          // a ControlledAsyncButton to handle the loading state, when all of the tasks are not on the same
          // status.
          <ControlledAsyncButton
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            disabled={!isActionable || disabled}
            isLoading={true}
            onClick={async () => {}}
            loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
            {...buttonProps}
          />
        ) : (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            dataTestAttrs={{component: 'WorksheetStatusButton', element: 'button'}}
            disabled={!isActionable || disabled}
            isDropdown={isWorksheetStatusActionable(displayStatus) || isDropdown}
            {...buttonProps}
          />
        )}
      </Dropdown>
    </>
  );
}

const styles = StyleSheet.create({
  dropdownDetails: {
    display: 'flex',
    cursor: 'default',
  },
  dropdownRightAligned: {
    position: 'absolute',
    right: 0, // Aligns the dropdown with the right edge of the button
    top: '100%', // Places the dropdown right below the button
    minHeight: '36px', // match ReviewBadge height without tooltip overriding
  },
  dropdownOptionContent: {
    display: 'flex',
    padding: '0px',
    marginRight: '8px',
    marginLeft: '8px',
  },
  statusText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: NEAR_BLACK,
    marginBottom: 4,
  },
  timestampText: {
    fontSize: '11px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '14px',
    color: GRAY_SEMIDARK,
  },
  // for dropdown options
  optionText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '16px',
    color: GRAY_DARKER,
  },
});
